import { useMemo } from "react";

export default function SeverityMeter({ score }) {
  const clampedScore = Math.min(10, Math.max(1, score));
  
  const { label, description, colorClass, bgClass } = useMemo(() => {
    if (clampedScore <= 3) {
      return {
        label: "Looking Good",
        description: "This message seems thoughtful and unlikely to cause misunderstanding. Nice work!",
        colorClass: "text-[#7BC4A4]",
        bgClass: "bg-[#7BC4A4]"
      };
    } else if (clampedScore <= 5) {
      return {
        label: "Worth a Second Look",
        description: "There are a few things that might be interpreted differently than you intend.",
        colorClass: "text-[#D4A853]",
        bgClass: "bg-[#D4A853]"
      };
    } else if (clampedScore <= 7) {
      return {
        label: "Let's Refine This",
        description: "We see some patterns that might create tension. Consider the alternatives below.",
        colorClass: "text-[#E09A5A]",
        bgClass: "bg-[#E09A5A]"
      };
    } else {
      return {
        label: "Pause and Reconsider",
        description: "This message might escalate things. Take a breath — there's a better way to express this.",
        colorClass: "text-[#E07A7A]",
        bgClass: "bg-[#E07A7A]"
      };
    }
  }, [clampedScore]);

  return (
    <div className="space-y-4" data-testid="severity-meter">
      {/* Score Display */}
      <div className="flex items-center gap-4">
        <div className={`text-4xl font-bold ${colorClass}`} data-testid="severity-score">
          {clampedScore}<span className="text-xl text-[#A0A7B4]">/10</span>
        </div>
        <div className={`text-lg font-semibold ${colorClass}`} data-testid="severity-label">
          {label}
        </div>
      </div>

      {/* Visual Meter */}
      <div className="relative h-3 rounded-full overflow-hidden bg-[#E5E7EB]">
        {/* Background gradient hint */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            background: 'linear-gradient(90deg, #7BC4A4 0%, #D4A853 50%, #E07A7A 100%)'
          }}
        />
        {/* Progress bar */}
        <div 
          className={`h-full rounded-full transition-all duration-700 ease-out ${bgClass}`}
          style={{ width: `${clampedScore * 10}%` }}
          data-testid="severity-bar"
        />
        {/* Score indicator */}
        <div 
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white shadow-soft transition-all duration-700"
          style={{ 
            left: `calc(${clampedScore * 10}% - 10px)`,
            border: `3px solid ${clampedScore <= 3 ? '#7BC4A4' : clampedScore <= 5 ? '#D4A853' : clampedScore <= 7 ? '#E09A5A' : '#E07A7A'}`
          }}
        />
      </div>

      {/* Scale labels */}
      <div className="flex justify-between text-xs text-[#A0A7B4] font-medium px-1">
        <span>Thoughtful</span>
        <span>Needs Work</span>
        <span>Risky</span>
      </div>

      {/* Description */}
      <div className="bg-[#F7F9FB] rounded-xl p-4 mt-4">
        <p className="text-sm text-[#6B7280] leading-relaxed" data-testid="severity-description">
          {description}
        </p>
      </div>
    </div>
  );
}
